/**
 * Explore EGM list: keep the order the user is looking at.
 *
 * The drawer list is sorted once, when a list is loaded for a sort the user
 * picked (insight + direction) and a scope they picked (casino, timeframe,
 * Favorites/Playlist/search/bank filter, compare set). After that, data that
 * comes in for the SAME sort and scope must not reorder the cards already on
 * screen:
 *  - the next infinite-scroll page,
 *  - a second emission of the same request (cached snapshot, then network),
 *  - a page-0 refetch caused by an unrelated effect resetting pageNumber.
 * Cards already shown keep their position (their values refresh in place) and
 * EGMs that were not shown yet are added at the end, in the order they arrived
 * (already sorted among themselves by the list request). Only a different sort
 * or scope (the user tapped a sort icon/shortcut, changed timeframe, filter or
 * casino) replaces the list with a freshly sorted one.
 *
 * Display-only: pure functions, no data fetching, no caches, no casino ids.
 */

export type ListScopeParams = {
  casinofk?: number | string | null;
  insight?: string | number | null;
  orderby?: string | number | null;
  timeframe?: string | number | null;
  slotnumbers?: string | null;
  compareSlotNumbers?: string | null;
};

const part = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim());

/**
 * Identity of "which sorted list is this". Deliberately leaves out pageNumber
 * and limit: pages of the same list share a scope, so they append instead of
 * replacing.
 */
export const listSortScopeKey = (p: ListScopeParams): string =>
  [
    part(p.casinofk),
    part(p.insight),
    part(p.orderby).toLowerCase(),
    part(p.timeframe),
    part(p.slotnumbers),
    part(p.compareSlotNumbers),
  ].join('|');

/**
 * Merge incoming rows into the rows already shown without reordering them.
 * - Shown rows keep their order; a shown row that is also incoming is swapped
 *   for the incoming (fresher) row object in the same position.
 * - Incoming rows not shown yet are appended in their incoming order.
 * - Duplicates (same key) are dropped, so overlapping pages never repeat a card.
 * - Rows without a key are kept where they are; incoming keyless rows are only
 *   taken when nothing is shown yet (they cannot be matched, so appending them
 *   on every refresh would duplicate them).
 */
export const appendWithoutReorder = <T>(
  shown: readonly T[] | null | undefined,
  incoming: readonly T[] | null | undefined,
  keyOf: (row: T) => string
): T[] => {
  const prev = shown || [];
  const next = incoming || [];
  const incomingByKey = new Map<string, T>();
  for (const row of next) {
    const k = keyOf(row);
    if (k && !incomingByKey.has(k)) incomingByKey.set(k, row);
  }
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of prev) {
    const k = keyOf(row);
    if (!k) {
      out.push(row);
      continue;
    }
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(incomingByKey.has(k) ? (incomingByKey.get(k) as T) : row);
  }
  const takeKeyless = prev.length === 0;
  for (const row of next) {
    const k = keyOf(row);
    if (!k) {
      if (takeKeyless) out.push(row);
      continue;
    }
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(row);
  }
  return out;
};

/**
 * Page to request for "load more". After a same-scope page-0 refresh merged
 * into a longer list, pageNumber is 0 again while several pages are already
 * shown; ask for the first page past what is loaded instead of page 1 again.
 */
export const nextListPageNumber = (
  currentPage: number,
  loadedCount: number,
  pageSize: number
): number => {
  const cur = Number.isFinite(currentPage) ? Math.max(0, Math.floor(currentPage)) : 0;
  if (!(pageSize > 0)) return cur + 1;
  const loadedPages = Math.floor(Math.max(0, loadedCount) / pageSize);
  return Math.max(cur + 1, loadedPages);
};
